import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ImageFallback } from '#app/components/image-fallback.tsx'
import { badgeVariants } from '#app/components/ui/badge.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	getLocalizedField,
	type SupportedLocale,
} from '#app/utils/blog-i18n.ts'
import { type getPublishedPosts } from '#app/utils/blog.server.ts'
import { useLocalizedPath } from '#app/utils/i18n.ts'
import { cn, getBlogImgSrc } from '#app/utils/misc.tsx'

export type PostWithRelations = Awaited<
	ReturnType<typeof getPublishedPosts>
>['posts'][number]

/**
 * The title link stretches over the whole card, so the card lifts as one
 * link. The category link sits above that overlay and stays its own target.
 */
export function BlogPostCard({
	post,
	lang,
	headingLevel: Heading = 'h2',
}: {
	post: PostWithRelations
	lang: string
	/** `h3` where the cards sit under a section heading */
	headingLevel?: 'h2' | 'h3'
}) {
	const { t } = useTranslation()
	const localePath = useLocalizedPath()
	const locale = lang as SupportedLocale
	const title = getLocalizedField(post, 'title', locale)
	const excerpt = getLocalizedField(post, 'excerpt', locale)

	return (
		<Card
			asChild
			interactive
			className="group relative h-full gap-0 overflow-hidden p-0"
		>
			<article>
				{/* Hero Image */}
				<div className="aspect-video overflow-hidden">
					{post.heroImage ? (
						<img
							src={getBlogImgSrc(post.heroImage.objectKey)}
							alt={post.heroImage.altText || title}
							className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
							loading="lazy"
						/>
					) : (
						<ImageFallback label={title} />
					)}
				</div>

				<div className="flex flex-1 flex-col p-5">
					{/* Category Badge */}
					{post.category && (
						<Link
							to={localePath(`/blog/category/${post.category.slug}`)}
							className={cn(
								badgeVariants({ variant: 'soft' }),
								'focus-cosy relative z-10 mb-2 self-start',
							)}
						>
							{getLocalizedField(post.category, 'name', locale)}
						</Link>
					)}

					{/* Title */}
					<Heading className="text-body-md mb-2 leading-snug font-semibold">
						<Link
							to={localePath(`/blog/${post.slug}`)}
							className="group-hover:text-primary transition-colors after:absolute after:inset-0"
						>
							{title}
						</Link>
					</Heading>

					{/* Excerpt */}
					{excerpt && (
						<p className="text-body-xs text-muted-foreground mb-4 line-clamp-2 flex-1 leading-relaxed">
							{excerpt}
						</p>
					)}

					{/* Meta */}
					<div className="border-border/30 text-body-2xs text-muted-foreground mt-auto flex items-center justify-between border-t pt-3">
						<span>
							{post.author.name || post.author.username}
							{post.publishedAt && (
								<>
									{' · '}
									<time dateTime={new Date(post.publishedAt).toISOString()}>
										{new Date(post.publishedAt).toLocaleDateString(
											lang === 'de'
												? 'de-CH'
												: lang === 'fr'
													? 'fr-CH'
													: 'en-GB',
											{ day: 'numeric', month: 'long', year: 'numeric' },
										)}
									</time>
								</>
							)}
						</span>
					</div>

					{/* Read More: an affordance only, the title link covers the card */}
					<span
						className="text-body-xs text-primary mt-3 inline-flex items-center font-medium"
						aria-hidden="true"
					>
						{t('blog.readMore')}
						<Icon
							name="arrow-right"
							className="ml-1 transition-transform group-hover:translate-x-0.5"
						/>
					</span>
				</div>
			</article>
		</Card>
	)
}
